import { UserRoundX } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { ROUTES } from '@/config/routes';

import { AuthSplitScreen } from './AuthSplitScreen';
import { BackToHomeLogo } from './BackToHomeLogo';
import { useAuth } from '../session';

/**
 * Says who is in the way, and offers the one thing that clears it.
 *
 * <p>The address is named rather than "somebody is signed in": the person reading this is
 * often not the person signed in, and the account they are about to sign out may not be
 * theirs to sign out. They can only make that call if they are told whose it is.
 */
export function SignOutFirst({ email }: { email: string }) {
  const { signOut } = useAuth();
  const [leaving, setLeaving] = useState(false);

  /*
   * Stays disabled after the call resolves, deliberately. Signing out re-renders this into
   * the page the link was for, and a button that went live again in between invites a second
   * click on a screen that is already going away.
   */
  const leave = () => {
    setLeaving(true);
    void signOut();
  };

  return (
    <AuthSplitScreen
      headline="One Account At A Time"
      blurb="This link belongs to an account, and the browser is already signed in to another. Signing out keeps the two from being mistaken for each other — nothing about the link is lost, and it opens as soon as the browser is free."
      illustration={<UserRoundX aria-hidden className="h-24 w-24 text-amber-500" />}
    >
      <div className="text-center">
        <BackToHomeLogo />
        <h1 className="mb-2 text-3xl font-black text-gray-900">Someone is already signed in</h1>
        <p className="text-gray-600">
          This browser is signed in as <span className="font-medium text-gray-900">{email}</span>.
          Sign that account out to open this link.
        </p>

        <div className="mt-8 space-y-3">
          <button
            type="button"
            onClick={leave}
            disabled={leaving}
            className="w-full rounded-md bg-blue-600 py-3 font-medium text-white transition-colors hover:bg-blue-700 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
          >
            {leaving ? 'Signing out…' : 'Sign out and continue'}
          </button>

          {/* The way out for somebody who did not mean to open this -- an admin who clicked
              a colleague's link. Without it the only offer on the page ends their session. */}
          <Link
            to={ROUTES.dashboard}
            className="flex w-full items-center justify-center rounded-md border border-gray-300 py-3 font-medium text-gray-700 transition-colors hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:outline-none"
          >
            Stay signed in as {email}
          </Link>
        </div>

        {/* Said before they click, not after. Somebody with work open in another tab needs
            to know that this ends it there too, while they can still go and save it. */}
        <p className="mt-6 text-sm text-gray-500">
          Signing out ends this session in every tab of this browser.
        </p>
      </div>
    </AuthSplitScreen>
  );
}
